
<script setup>
import { onMounted, ref, watch, nextTick, onUnmounted, computed } from "vue";
import axios from "@/utils/axios";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useLoading } from "vue-loading-overlay";
import loaderData from "@/utils/loader-data";
import { useRouter } from "vue-router";
import interact from "interactjs";
import AppDateTimePicker from "@/@core/components/app-form-elements/AppDateTimePicker.vue";
import DeleteConfirm from "@/components/common/DeleteConfirm.vue";

const router = useRouter();
const $loading = useLoading(loaderData);

const companyData = ref({
  title: "",
  description: "",
  notes: "",
  address: "",
  address_2: "",
  contact_name: "",
  contact_phone: "",
  contact_email: "",
  general_phone: "",
  general_email: "",
  branch_codes: [],
  rep_codes: [],
  values: [],
  fields_temp_id: "",
});

const route = useRoute("investment-companies-id");
const submitBtn = ref(1);
const companyActiveTab = ref(0);

const customFormFields = ref([]);
const system_form_field_types = ref([]);
const canManageCustomFields = ref(false);
const rolesMapping = ref([]);
const rolesForField = ref([]);
const branchesForField = ref([]);
const lookup_fields = ref([]);
const modules = ref([]);
const uniqueCreationID = ref("");
const customFieldDialog = ref(false);
const customFieldDialogTab = ref(1);
const customFormFieldInstance = ref({});
const currentFieldOptionValue = ref("");
const deleteFormFieldDialog = ref(false);
const deleteFormFieldId = ref("");

const branchCodes = ref();
const repCodes = ref();

onUnmounted(() => {
  interact(".draggable-col-investment-companies-edit").unset();
  interact(".form_field_create_investment_companies").unset();
  interact(".form_fields_dropzone_investment_companies_edit").unset();
});

const getInvestmentCompanyData = function () {
  let id = route.params.id;
  let loader = $loading.show();
  let url = `/investment_companies/${id}`;
  axios
    .get(url)
    .then((response) => {
      if (response.status == "success") {
        companyData.value = response.data.investment_company;
        companyData.value.values = response.data.investment_company.custom_fields || [];
        if (!companyData.value.fields_temp_id && uniqueCreationID.value) {
          companyData.value.fields_temp_id = uniqueCreationID.value;
        }

        branchCodes.value = response.data.investment_company.branch_codes;
        repCodes.value = response.data.investment_company.rep_codes;
        let branches = [];
        branchCodes.value.forEach((item) => {
          item.branches.forEach((itemBranch) => {
            branches.push(itemBranch);
          });
        });

        repCodes.value.forEach((item) => {
          branches.push(item.branch_id);
        });

        fetchBranchesForField("", companyData.value.branches);
        let include_users = [];
        repCodes.value.forEach((item) => {
          item.users.forEach((itemUser) => {
            include_users.push(itemUser);
          });
        });
        fetchUsersForField("", include_users);
      } else {
        toast.error(response.message, {
          autoClose: 2000,
        });
      }
    })
    .catch((error) => {
      loader.hide();
    })
    .finally(() => {
      loader.hide();
    });
};

const canSeeManageCustomFieldsButton = () => {
  const userData = JSON.parse(localStorage.getItem('userData'));
  const userPermissionsByRoles = userData.permissions_by_roles;
  canManageCustomFields.value = Object.values(userPermissionsByRoles).some(
    (permissions) => permissions.includes("Investment Company Custom Fields")
  );
};

const getSystemFormFieldTypes = () => {
  axios
    .get(`/system_form_field_types`)
    .then((response) => {
      if (response.status == "success") {
        system_form_field_types.value = response.data.field_types;
      }
    })
    .catch(() => {})
    .finally(() => {});
};

const getCustomFormFields = () => {
  axios
    .get(`/custom_fields/investment_companies`)
    .then((response) => {
      if (response.status == "success") {
        customFormFields.value = response.data.form_fields;
      }
      nextTick(() => {
        reorderFields();
      });
    })
    .catch(() => {})
    .finally(() => {});
};

const resolveIconForFieldType = (system_form_field) => {
  if (system_form_field.type == "single_line") {
    return "tabler-cursor-text";
  } else if (system_form_field.type == "multi_line") {
    return "tabler-forms";
  } else if (system_form_field.type == "integer") {
    return "tabler-123";
  } else if (system_form_field.type == "percent") {
    return "tabler-percentage";
  } else if (system_form_field.type == "decimal") {
    return "tabler-decimal";
  } else if (system_form_field.type == "currency") {
    return "tabler-cash";
  } else if (system_form_field.type == "date") {
    return "tabler-calendar-month";
  } else if (system_form_field.type == "date_time") {
    return "tabler-calendar-clock";
  } else if (system_form_field.type == "email") {
    return "tabler-mail";
  } else if (system_form_field.type == "phone") {
    return "tabler-phone";
  } else if (system_form_field.type == "pick_list") {
    return "tabler-checkup-list";
  } else if (system_form_field.type == "multi_select") {
    return "tabler-list-check";
  } else if (system_form_field.type == "checkbox") {
    return "tabler-checkbox";
  }
  return "";
};

const fetchRoles = () => {
  axios
    .get(`/get-all-roles-list`)
    .then((response) => {
      if (response.status == "success") {
        rolesForField.value = response.data.roles.map((role) => ({
          title: role.readable_name,
          value: role.id,
        }));
      }
    })
    .catch(() => {})
    .finally(() => {});
};

const getLookupFields = () => {
  axios
    .get("lookup_fields")
    .then((response) => {
      if (response.status == "success") {
        lookup_fields.value = response.data.lookup_fields;
        modules.value = Object.keys(response.data.lookup_fields).map(key => ({
          title: response.data.lookup_fields[key].name,
          value: key,
        }));
      }
    })
    .catch(() => {})
    .finally(() => {});
};

const lookupFieldAttributes = computed(() => {
  let attributes = [];
  if (customFormFieldInstance.value.table) {
    let lookupFieldColumns = lookup_fields.value[customFormFieldInstance.value.table].columns;
    attributes = Object.keys(lookupFieldColumns).map(key => ({
      title: lookupFieldColumns[key],
      value: key,
    }));
    let attributeKeys = Object.keys(lookupFieldColumns);
    if (!attributeKeys.includes(customFormFieldInstance.value.column)) {
      customFormFieldInstance.value.column = null;
    }
  }
  return attributes;
});

const fieldLookupSettingChanged = () => {
  customFormFieldInstance.value.options = [];
};

const searchBranchesForField = (event) => {
  let branches = customFormFieldInstance.value.branches.map(item => item.value ?? item);
  fetchBranchesForCustomFields(event.target.value, branches);
};

const searchBranchesForFieldOnChange = () => {
  let branches = [];
  customFormFieldInstance.value.branches.forEach((branch) => {
    if (branch.value) {
      branches.push(branch.value);
    } else {
      branches.push(branch);
    }
  });
  fetchBranchesForCustomFields('', branches);
};

const fetchBranchesForCustomFields = (search = '', includes = []) => {
  let includesToArr = includes.join(',');
  axios
    .get(`/branches?search_query=${search}&includes=${includesToArr}`)
    .then((response) => {
      if (response.status == "success") {
        branchesForField.value = response.data.branches.data.map((branch) => ({
          title: branch.branch_name,
          value: branch.id,
        }));
      }
    })
    .catch(() => {})
    .finally(() => {});
};

const addCustomFormField = (type) => {
  customFieldDialog.value = true;
  customFieldDialogTab.value = 0;
  customFormFieldInstance.value = {
    name: "",
    type: type.type,
    meta: {
      layer_option: {
        branches: 'custom',
        roles: 'none',
      },
      temp_id: uniqueCreationID.value,
      current_branch: true,
    },
    conditions: [],
    options: [],
    branches: [],
    roles: [],
    is_required: false,
  };
};

const saveFormFieldChanges = async () => {
  let loader = $loading.show();
  axios
    .post('/custom_fields/investment_companies/create_update_field', {
      form_field: customFormFieldInstance.value,
    })
    .then((response) => {
      if (response.status == "success") {
        toast.success(response.message, { autoClose: 5000 });
        customFieldDialog.value = false;
        customFieldDialogTab.value = 0;
        getCustomFormFields();
        nextTick(() => {
          reorderFields();
        });
      } else {
        toast.error(response.message, { autoClose: 2000 });
      }
    })
    .catch(() => {})
    .finally(() => {
      loader.hide();
    });
};

const addFormFieldOption = () => {
  if (currentFieldOptionValue.value != "") {
    let currentOptions = customFormFieldInstance.value.options;
    currentOptions.push(currentFieldOptionValue.value);
    customFormFieldInstance.value.options = currentOptions;
    currentFieldOptionValue.value = "";
  }
};

const removeFormFieldOption = (index) => {
  if (customFormFieldInstance.value.options[index].id) {
    axios
      .delete(
        "/helpdesk/form_field_options/" + `${customFormFieldInstance.value.options[index].id}`
      )
      .then((response) => {
        if (response.status == "success") {
          customFormFieldInstance.value.options.splice(index, 1);
        }
      })
      .catch(() => {})
      .finally(() => {});
  } else {
    customFormFieldInstance.value.options.splice(index, 1);
  }
};

const removeFormField = (form_field_id) => {
  deleteFormFieldId.value = form_field_id;
  deleteFormFieldDialog.value = true;
};

const removeFormFieldSubmit = () => {
  let loader = $loading.show();
  axios
    .delete("/helpdesk/form_fields/" + `${deleteFormFieldId.value}`)
    .then((response) => {
      if (response.status == "success") {
        toast.success(response.message, { autoClose: 5000 });
        getCustomFormFields();
        deleteFormFieldDialog.value = false;
      } else {
        toast.error(response.message, { autoClose: 2000 });
      }
    })
    .catch(() => {})
    .finally(() => {
      loader.hide();
    });
};

const editFieldSettings = (form_field) => {
  customFormFieldInstance.value = form_field;
  customFieldDialogTab.value = 0;
  customFieldDialog.value = true;
};
const isFieldsReady = ref(false);
const fieldOrders = ref([]);

const makeFieldsDraggable = () => {
  interact(".form_field_create_investment_companies").draggable({
    autoScroll: true,
    onstart: () => {},
    onmove: (event) => {
      const target = event.target;
      const x = (parseFloat(target.getAttribute("data-x")) || 0) + event.dx;
      const y = (parseFloat(target.getAttribute("data-y")) || 0) + event.dy;
      target.style.transform = "translate(" + x + "px, " + y + "px)";
      target.setAttribute("data-x", x);
      target.setAttribute("data-y", y);
    },
    onend: (event) => {
      const target = event.target;
      const dropzone = event.dropzone;
      if (!dropzone) {
        target.style.transform = `translate(0px, 0px)`;
        target.setAttribute("data-x", 0);
        target.setAttribute("data-y", 0);
      }
    },
  });
};

const initializeDropZone = () => {
  interact(".form_fields_dropzone_investment_companies_edit").dropzone({
    accept: ".form_field_create_investment_companies",
    overlap: 0.1,
    ondragenter: function (event) {
      event.target.classList.add("drop-area-bg");
    },
    ondragleave: function (event) {
      event.target.classList.remove("drop-area-bg");
    },
    ondrop: (event) => {
      event.target.classList.remove("drop-area-bg");
      const droppedElement = event.relatedTarget;
      const type = droppedElement.getAttribute("type");
      if (type) {
        const fieldType = system_form_field_types.value.find(
          (item) => item.type === type
        );
        if (fieldType) {
          addCustomFormField(fieldType);
        }
      }
      droppedElement.style.transform = `translate(0px, 0px)`;
      droppedElement.setAttribute("data-x", "0px");
      droppedElement.setAttribute("data-y", "0px");
    },
  });
};
onMounted(async () => {
  try {
    const userData = JSON.parse(localStorage.getItem('userData'));
    const userPermissionsByRoles = userData.permissions_by_roles;
    canManageCustomFields.value = Object.values(userPermissionsByRoles).some(
      (permissions) => permissions.includes("Investment Company Custom Fields")
    );

    const timestamp = Date.now();
    const randomNum = Math.floor(Math.random() * 1000000);
    uniqueCreationID.value = `${timestamp}-${randomNum}`;

    // Fetch both field orders and custom fields
    const fieldOrdersResponse = await axios.get(
      "/module-fields/investment_companies"
    );

    if (fieldOrdersResponse.status === "success") {
      fieldOrders.value = fieldOrdersResponse.data.field_orders;
      isFieldsReady.value = true;

      // Wait for DOM to update before reordering
      await nextTick();
      reorderFields();
    }
  } catch (error) {
    console.error("Error initializing fields:", error);
  }

  getSystemFormFieldTypes();
  getCustomFormFields();
  getLookupFields();
  fetchRoles();

  axios
    .get("get-all-roles-by-id")
    .then((response) => {
      if (response.status == "success") {
        rolesMapping.value = response.data.roles;
      }
    })
    .catch(() => {})
    .finally(() => {});

  interact(".draggable-col-investment-companies-edit")
    .draggable({
      autoScroll: true,
      onstart: (event) => {},
      onmove: (event) => {
        var target = event.target;
        var x = (parseFloat(target.getAttribute("data-x")) || 0) + event.dx;
        var y = (parseFloat(target.getAttribute("data-y")) || 0) + event.dy;
        target.style.transform = "translate(" + x + "px, " + y + "px)";
        target.setAttribute("data-x", x);
        target.setAttribute("data-y", y);
      },
      onend: (event) => {
        const target = event.target;
        const dropzone = event.dropzone;

        if (!dropzone) {
          target.style.transform = `translate(0px, 0px)`;
          target.setAttribute("data-x", 0);
          target.setAttribute("data-y", 0);
        }
      },
    })
    .dropzone({
      accept: ".draggable-col-investment-companies-edit",
      overlap: 0.1,
      ondragenter: function (event) {
        event.target.classList.add("drop-area-bg");
      },
      ondragleave: function (event) {
        event.target.classList.remove("drop-area-bg");
      },
      ondrop: (event) => {
        event.target.classList.remove("drop-area-bg");
        const draggedElement = event.relatedTarget;
        const targetElement = event.target;

        const draggedOrder = draggedElement.getAttribute("data-order");
        const targetOrder = targetElement.getAttribute("data-order");

        // update the field order
        axios
          .post("/module-fields/investment_companies/reorder", {
            dragged_field: draggedOrder,
            target_field: targetOrder,
          })
          .then((response) => {
            if (response.status == "success") {
              console.log("Field order updated successfully");
              fieldOrders.value = response.data.field_orders;
              nextTick(() => {
                reorderFields();
              });
            }
          })
          .catch((error) => {
            console.error("Error updating field order:", error);
          });

        draggedElement.style.transform = `translate(0px, 0px)`;
        draggedElement.setAttribute("data-x", "0px");
        draggedElement.setAttribute("data-y", "0px");
      },
    });

  if (canManageCustomFields.value) {
    makeFieldsDraggable();
    initializeDropZone();
  }

  getInvestmentCompanyData();
});


const reorderFields = () => {
    const formRow = document.querySelector('.fields_row')
    if (!formRow || !fieldOrders.value?.fields) return

    // Get all field elements
    const allFieldElements = formRow.querySelectorAll('.draggable-col-investment-companies-edit')
    if (!allFieldElements.length) return
    
    // Sort fields array by display_order
    const sortedFields = [...fieldOrders.value.fields].sort((a, b) => a.display_order - b.display_order)
    
    // Track which elements have been ordered
    const orderedElements = new Set()
    
    // First reorder elements that exist in fieldOrders
    sortedFields.forEach((fieldData) => {
        const fieldElement = formRow.querySelector(`[data-order="${fieldData.field}"]`)
        if (fieldElement) {
            formRow.appendChild(fieldElement)
            orderedElements.add(fieldElement)
        }
    })
    
    // Append any remaining elements (new fields) to the end
    allFieldElements.forEach(element => {
        if (!orderedElements.has(element)) {
            formRow.appendChild(element)
        }
    })
}

// Add a watch for fieldOrders to ensure reordering happens when data changes
watch(
    fieldOrders,
    () => {
        if (isFieldsReady.value) {
            nextTick(() => {
                reorderFields()
            })
        }
    },
    { deep: true }
)

const findFormFieldValue = (fieldId) => {
  return companyData.value.values.find(field => field.form_field_id === fieldId) || { value: '' };
};

const transformedOptions = (form_field) => {
  return form_field.options.map((option) => ({
    title: option.name,
    value: option.id,
  }));
};

const showfield = (form_field) => {
  let conditions = form_field.conditions;
  let condition_status = false;
  if (conditions.length > 0) {
    conditions.forEach((condition) => {
      let parentField = companyData.value.values.find(field => field.form_field_id === condition.parent_form_field_id);
      if (parentField) {
        let parent_value = parentField.value;
        let parent_options = customFormFields.value.find(field => field.id === condition.parent_form_field_id)?.options || [];
        if (parent_value) {
          let parent_selected_option = parent_options.find(option => option.id == parent_value);
          if (parent_selected_option && parent_selected_option.name == condition.value) {
            condition_status = true;
          }
        }
      }
    });
  } else {
    condition_status = true;
  }

  let layers_status = false;
  let form_field_layer_option_branch = form_field.meta?.layer_option?.branches;
  let form_field_layer_option_role = form_field.meta?.layer_option?.roles;
  const hasBranchRule = form_field_layer_option_branch && form_field_layer_option_branch !== 'none';
  const hasRoleRule = form_field_layer_option_role && form_field_layer_option_role !== 'none';

  if (!hasBranchRule && !hasRoleRule) {
    layers_status = true;
  }

  if (form_field_layer_option_branch == 'all') {
    layers_status = true;
  } else if (form_field_layer_option_branch == 'custom') {
    const companyBranches = Array.isArray(companyData.value.branches)
      ? companyData.value.branches
      : [];
    const fieldBranches = Array.isArray(form_field.branches)
      ? form_field.branches
      : [];
    const hasMatchingBranch = companyBranches.some((branchId) =>
      fieldBranches.includes(branchId)
    );
    if (hasMatchingBranch || form_field.meta?.current_branch) {
      layers_status = true;
    }
  }

  const userData = JSON.parse(localStorage.getItem('userData'));
  let selected_role = rolesMapping.value[userData?.role];
  if (form_field_layer_option_role == 'all' && selected_role) {
    layers_status = true;
  } else if (form_field_layer_option_role == 'custom' && Array.isArray(form_field.roles) && form_field.roles.includes(selected_role)) {
    layers_status = true;
  }

  return condition_status && layers_status;
};

const updateUserCustomFields = () => {
  let custom_fields = customFormFields.value;
  let companyValues = companyData.value.values;
  custom_fields.forEach((custom_field) => {
    if (showfield(custom_field)) {
      let exists = false;
      companyValues.forEach((companyValue) => {
        if (companyValue.form_field_id == custom_field.id) {
          exists = true;
        }
      });

      if (!exists) {
        if (custom_field.type == 'multi_select') {
          companyData.value.values.push({
            form_field_id: custom_field.id,
            value: [],
          });
        } else {
          companyData.value.values.push({
            form_field_id: custom_field.id,
            value: '',
          });
        }
      } else if (custom_field.type == 'multi_select') {
        let allOptionsAreValid = true;
        let newCompanyValues = companyValues.map((companyValue) => {
          if (companyValue.form_field_id == custom_field.id) {
            let selected_options = companyValue.value.map((item) => item);
            let available_options = custom_field.options.map((item) => item.id);
            let valid_selected_options = selected_options.filter(option => available_options.includes(option));
            if (valid_selected_options.length != selected_options.length) {
              allOptionsAreValid = false;
            }
            return {
              form_field_id: companyValue.form_field_id,
              value: valid_selected_options,
            };
          }
          return companyValue;
        });
        if (!allOptionsAreValid) {
          companyData.value.values = newCompanyValues;
        }
      }
    }
  });
};

watch(
  customFormFields,
  async () => {
    updateUserCustomFields();
  },
  { deep: true }
);

watch(
  companyData,
  async () => {
    updateUserCustomFields();
  },
  { deep: true }
);

const customFieldPickListUpdatedExecute = (form_field) => {
  if (form_field.is_lookup_field && (form_field.parent_lookup_field_id == 0 || form_field.act_as_parent)) {
    let parentField = companyData.value.values.find((value) => value.form_field_id == form_field.id);
    let parent_value = parentField ? parentField.value : null;
    if (parent_value) {
      axios
        .get(
          "/custom_fields/get_lookup_field_record" + `?table=${form_field.table}&id=${findFormFieldValue(form_field.id).value}`
        )
        .then((response) => {
          if (response.status == "success") {
            customFormFields.value.forEach((customField) => {
              if (customField.parent_lookup_field_id == form_field.id) {
                customField.options = response.data.options;
              }
            });
          }
        })
        .catch(() => {})
        .finally(() => {});
    }
  }
};

const customFieldPickListUpdated = (form_field) => {
  setTimeout(() => {
    customFieldPickListUpdatedExecute(form_field);
  }, 50);
};

const formatCustomPhoneField = (value) => {
  let numericValue = value.replace(/\D/g, '');
  if (numericValue.length > 10) {
    numericValue = numericValue.slice(0, 10);
  }
  if (numericValue.length > 3 && numericValue.length <= 6) {
    numericValue = numericValue.replace(/^(\d{3})(\d{0,3})/, '$1-$2');
  } else if (numericValue.length > 6) {
    numericValue = numericValue.replace(/^(\d{3})(\d{3})(\d{0,4})/, '$1-$2-$3');
  }
  return numericValue;
};

const onCustomPhoneInput = (event, fieldId) => {
  const formattedValue = formatCustomPhoneField(event.target.value);
  const fieldValue = companyData.value.values.find(field => field.form_field_id === fieldId);
  if (fieldValue) {
    fieldValue.value = formattedValue;
  }
};

const dateTimePickerConfig = {
  dateFormat: 'm/d/Y',
  allowInput: true,
  maxDate: new Date().toLocaleDateString('en-US'),
};


const updateInvestmentCompany = async () => {
  let loader = $loading.show();
  submitBtn.value = 0;

  let formData = companyData.value;
  delete formData.branch_codes;
  delete formData.rep_codes;
  formData.values = companyData.value.values;

  axios
    .patch("/investment_companies/" + `${companyData.value.id}`, formData)
    .then((response) => {
      if (response.status == "success") {
        toast.success(response.message, {
          autoClose: 5000,
        });
        router.push({ name: "investment-companies-list" });
      } else {
        toast.error(response.message, {
          autoClose: 2000,
        });
      }
    })
    .catch((error) => {
      loader.hide();
    })
    .finally(() => {
      loader.hide();
      submitBtn.value = 1;
    });
};

const branchList = ref([]);
const userList = ref([]);

const searchBranches = (event) => {
  let branches = [];
  branchCodes.value.forEach((item) => {
    item.branches.forEach((itemBranch) => {
      branches.push(itemBranch);
    });
  });
  repCodes.value.forEach((item) => {
    branches.push(item.branch_id);
  });
  fetchBranchesForField(event.target.value, branches);
};

const searchUsers = (event) => {
  let include_users = [];
  repCodes.value.forEach((item) => {
    item.users.forEach((itemUser) => {
      include_users.push(itemUser);
    });
  });
  fetchUsersForField(event.target.value, include_users);
};

const fetchBranchesForField = (search = "", includes = []) => {
  let includes_to_arr = includes.join(",");
  axios
    .get(`/branches?search_query=${search}&includes=${includes_to_arr}`)
    .then((response) => {
      if (response.status == "success") {
        branchList.value = response.data.branches.data.map((item) => {
          return {
            title: item.branch_name,
            value: item.id,
          };
        });
      }
    })
    .catch((error) => {})
    .finally(() => {});
};

const fetchUsersForField = (search = "", includes = []) => {
  let includes_to_arr = includes.join(",");
  axios
    .get(
      `/users?search_query=${search}&includes=${includes_to_arr}&roles=representative_manager,representative`
    )
    .then((response) => {
      if (response.status == "success") {
        userList.value = response.data.users.data.map((item) => {
          return {
            title: item.first_name + " " + item.last_name,
            value: item.id,
          };
        });
      }
    })
    .catch((error) => {})
    .finally(() => {});
};

const validateUsers = (index) => {
  let newlist = repCodes.value[index].users.filter((instanceItem) =>
    userList.value.some((userItem) => userItem.value === instanceItem)
  );
  repCodes.value[index].users = newlist;
};

const validateBranches = (index) => {
  let newlist = branchCodes.value[index].branches.filter((instanceItem) =>
    branchList.value.some((branchItem) => branchItem.value === instanceItem)
  );
  branchCodes.value[index].branches = newlist;
};

const discardChanges = () => {
  router.push({ name: "investment-companies-list" });
};

const addBranchCode = () => {
  branchCodes.value.push({
    title: "",
    description: "",
    branch_code: "",
    branches: [],
    temp_id: Math.random().toString(36).substr(2, 9),
  });
};

const removeBranchCode = (index) => {
  // delete via API if id exists
  if (branchCodes.value[index].id) {
    let loader = $loading.show();
    axios
      .delete(
        "/investment_companies_branch_codes/" + `${branchCodes.value[index].id}`
      )
      .then((response) => {
        if (response.status == "success") {
          toast.success(response.message, {
            autoClose: 2000,
          });
          updateRepCodesBranch(branchCodes.value[index].id);
          branchCodes.value.splice(index, 1);
        } else {
          toast.error(response.message, {
            autoClose: 2000,
          });
        }
      })
      .catch((error) => {
        loader.hide();
      })
      .finally(() => {
        loader.hide();
      });
  } else {
    updateRepCodesBranch(branchCodes.value[index].temp_id);
    branchCodes.value.splice(index, 1);
  }
};

const updateRepCodesBranch = (id) => {
  repCodes.value.forEach((repCode, index) => {
    if (repCode.investment_company_branch_code_id == id) {
      repCodes.value[index] = "";
    }
  });
};

const addRepCode = () => {
  repCodes.value.push({
    title: "",
    description: "",
    rep_code: "",
    users: [],
    investment_company_branch_code_id: "",
  });
};

const removeRepCode = (index) => {
  repCodes.value.splice(index, 1);
};

// watch(branchCodes, (newBranchCodes, oldBranchCodes) => {
//     console.log(newBranchCodes, 'newBranchCodes')
//     console.log(repCodes.value, 'repCodes.value')
//   // repCodes.value.forEach((repCode, index) => {

//   // })
// }, { deep: true })

const onPhoneInput = (event) => {
  let value = event.target.value.replace(/[^\d]/g, '');
  value = value.slice(0, 10);
  if (value.length >= 3) {
    value = value.slice(0, 3) + '-' + value.slice(3);
    if (value.length >= 7) {
      value = value.slice(0, 7) + '-' + value.slice(7);
    }
  }
  companyData.value[event.target.name] = value;
};

const phoneValidator = (value) => {
  const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
  return phoneRegex.test(value) || !value || 'Please enter a valid phone number (XXX-XXX-XXXX)';
};

definePage({ meta: { requiresAuth: true } });
</script>

<template>
  <div>
    <div
      class="d-flex flex-wrap justify-start justify-sm-space-between gap-y-4 gap-x-6 mb-6"
    >
      <div class="d-flex flex-column justify-center">
        <h2>
          <strong class="oswald">Edit Investment Company</strong>
        </h2>
        <span class="lato-light"
          >You can edit investment company on this page</span
        >
      </div>

      <div class="d-flex gap-4 align-center flex-wrap">
        <VBtn variant="tonal" color="dark" @click.prevent="discardChanges">
          Discard
        </VBtn>
        <VBtn @click.prevent="updateInvestmentCompany"> Update </VBtn>
      </div>
    </div>

    <VRow>
      <VCol cols="12" :md="canManageCustomFields ? 9 : 12">
        <VCard class="mb-6 form_fields_dropzone_investment_companies_edit" title="">
          <VCardText>
            <VForm class="" @submit.prevent="updateInvestmentCompany">
              <VTabs v-model="companyActiveTab" class=" ">
                <VTab>Company Information</VTab>
                <VTab>Branch Codes</VTab>
                <VTab>Rep Codes</VTab>
              </VTabs>

              <VWindow v-model="companyActiveTab">
                <VWindowItem :key="0" :value="`item-${0}`">
                  <div class="py-5">
                    <VRow class="fields_row pa-1 ma-1">
                      <VCol
                        class="draggable-col-investment-companies-edit"
                        cols="12"
                        md="6"
                        data-order="title"
                      >
                        <AppTextField
                          v-model="companyData.title"
                          label="Title *"
                          placeholder="Title"
                          :rules="[requiredValidator]"
                        />
                      </VCol>

                      <VCol
                        class="draggable-col-investment-companies-edit"
                        cols="12"
                        md="12"
                        data-order="description"
                      >
                        <AppTextarea
                          v-model="companyData.description"
                          rows="2"
                          label="Description *"
                          placeholder="Description"
                          :rules="[requiredValidator]"
                        />
                      </VCol>

                      <!-- <VCol
                        cols="12"
                        md="6"
                      >
                        <AppCombobox
                          v-model="companyData.branches"
                          :items="branchList"
                          label="Select Branches"
                          placeholder="Type to search.."
                          :return-object="false"
                          clearable
                          multiple
                          chips
                          closable-chips
                          clear-icon="tabler-x"
                          @keyup="searchBranches"
                          @blur="validateBranches"
                        />
                      </VCol> -->

                      <VCol
                        class="draggable-col-investment-companies-edit"
                        cols="12"
                        md="6"
                        data-order="contact_name"
                      >
                        <AppTextField
                          v-model="companyData.contact_name"
                          label="Contact Name"
                          placeholder="Contact Name"
                        />
                      </VCol>

                      <VCol
                        class="draggable-col-investment-companies-edit"
                        cols="12"
                        md="6"
                        data-order="contact_phone"
                      >
                        <AppTextField
                          v-model="companyData.contact_phone"
                          name="contact_phone"
                          label="Contact Phone"
                          placeholder="Contact Phone"
                          @input="onPhoneInput"
                          :rules="[phoneValidator]"
                        />
                      </VCol>

                      <VCol
                        class="draggable-col-investment-companies-edit"
                        cols="12"
                        md="6"
                        data-order="contact_email"
                      >
                        <AppTextField
                          v-model="companyData.contact_email"
                          label="Contact Email"
                          placeholder="Contact Email"
                        />
                      </VCol>

                      <VCol
                        class="draggable-col-investment-companies-edit"
                        cols="12"
                        md="6"
                        data-order="general_phone"
                      >
                        <AppTextField
                          v-model="companyData.general_phone"
                          name="general_phone"
                          label="General Phone"
                          placeholder="General Phone"
                          @input="onPhoneInput"
                          :rules="[phoneValidator]"
                        />
                      </VCol>

                      <VCol
                        class="draggable-col-investment-companies-edit"
                        cols="12"
                        md="6"
                        data-order="general_email"
                      >
                        <AppTextField
                          v-model="companyData.general_email"
                          label="General Email"
                          placeholder="General Email"
                        />
                      </VCol>

                      <VCol
                        class="draggable-col-investment-companies-edit"
                        cols="12"
                        md="12"
                        data-order="address"
                      >
                        <AppTextField
                          v-model="companyData.address"
                          label="Address Line 1"
                          placeholder="Address Line 1"
                        />
                      </VCol>

                      <VCol
                        class="draggable-col-investment-companies-edit"
                        cols="12"
                        md="12"
                        data-order="address_2"
                      >
                        <AppTextField
                          v-model="companyData.address_2"
                          label="Address Line 2"
                          placeholder="Address Line 2"
                        />
                      </VCol>

                      <VCol
                        class="draggable-col-investment-companies-edit"
                        cols="12"
                        md="12"
                        data-order="notes"
                      >
                        <AppTextarea
                          v-model="companyData.notes"
                          rows="2"
                          label="Notes"
                          placeholder="Notes"
                        />
                      </VCol>

                      <VCol
                        v-for="form_field in customFormFields"
                        :key="form_field.id"
                        v-show="showfield(form_field)"
                        :data-order="form_field?.id"
                        class="draggable-col-investment-companies-edit"
                        cols="12"
                        :md="form_field?.type == 'multi_line' ? '12' : '6'"
                      >
                        <div v-if="form_field.type == 'single_line'" class="rounded pa-3" style="position:relative; background-color: rgba(
                          var(--v-theme-on-background),
                          var(--v-hover-opacity)
                        ) !important;">
                          <AppTextField
                            variant="plain"
                            v-model="findFormFieldValue(form_field.id).value"
                            :label="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :placeholder="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :rules="form_field.is_required ? [requiredValidator] : []"
                          />
                          <div style="position: absolute; top: -10px; right: 10px;" v-if="canManageCustomFields">
                            <VBtn
                              @click.prevent="editFieldSettings(form_field)"
                              class="mr-1"
                              size="25"
                              icon="tabler-settings"
                              color="dark"
                              rounded
                              variant="tonal"
                            />
                            <VBtn
                              color="dark"
                              @click.prevent="removeFormField(form_field.id)"
                              size="25"
                              icon="tabler-minus"
                              rounded
                              variant="tonal"
                            />
                          </div>
                        </div>
                        <div v-else-if="form_field.type == 'multi_line'" class="rounded pa-3" style="position: relative; background-color: rgba(
                          var(--v-theme-on-background),
                          var(--v-hover-opacity)
                        ) !important;">
                          <AppTextarea
                            v-model="findFormFieldValue(form_field.id).value"
                            rows="2"
                            :label="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :placeholder="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :rules="form_field.is_required ? [requiredValidator] : []"
                          />
                          <div style="position: absolute; top: -10px; right: 10px;" v-if="canManageCustomFields">
                            <VBtn
                              @click.prevent="editFieldSettings(form_field)"
                              class="mr-1"
                              size="25"
                              icon="tabler-settings"
                              color="dark"
                              rounded
                              variant="tonal"
                            />
                            <VBtn
                              color="dark"
                              @click.prevent="removeFormField(form_field.id)"
                              size="25"
                              icon="tabler-minus"
                              rounded
                              variant="tonal"
                            />
                          </div>
                        </div>
                        <div v-else-if="form_field.type == 'integer'" class="rounded pa-3" style="position: relative; background-color: rgba(
                          var(--v-theme-on-background),
                          var(--v-hover-opacity)
                        ) !important;">
                          <AppTextField
                            v-model="findFormFieldValue(form_field.id).value"
                            type="number"
                            :label="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :placeholder="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :rules="form_field.is_required ? [requiredValidator, integerValidator] : [integerValidator]"
                          />
                          <div style="position: absolute; top: -10px; right: 10px;" v-if="canManageCustomFields">
                            <VBtn
                              @click.prevent="editFieldSettings(form_field)"
                              class="mr-1"
                              size="25"
                              icon="tabler-settings"
                              color="dark"
                              rounded
                              variant="tonal"
                            />
                            <VBtn
                              color="dark"
                              @click.prevent="removeFormField(form_field.id)"
                              size="25"
                              icon="tabler-minus"
                              rounded
                              variant="tonal"
                            />
                          </div>
                        </div>
                        <div v-else-if="form_field.type == 'percent'" class="rounded pa-3" style="position: relative; background-color: rgba(
                          var(--v-theme-on-background),
                          var(--v-hover-opacity)
                        ) !important;">
                          <AppTextField
                            v-model="findFormFieldValue(form_field.id).value"
                            type="number"
                            step="0.1"
                            :label="form_field.is_required ? form_field.name + ' * (%)' : form_field.name + ' (%)'"
                            :placeholder="form_field.is_required ? form_field.name + ' * (%)' : form_field.name + ' (%)'"
                            :rules="form_field.is_required ? [requiredValidator, minValidator, maxValidator] : [minValidator, maxValidator]"
                          />
                          <div style="position: absolute; top: -10px; right: 10px;" v-if="canManageCustomFields">
                            <VBtn
                              @click.prevent="editFieldSettings(form_field)"
                              class="mr-1"
                              size="25"
                              icon="tabler-settings"
                              color="dark"
                              rounded
                              variant="tonal"
                            />
                            <VBtn
                              color="dark"
                              @click.prevent="removeFormField(form_field.id)"
                              size="25"
                              icon="tabler-minus"
                              rounded
                              variant="tonal"
                            />
                          </div>
                        </div>
                        <div v-else-if="form_field.type == 'decimal'" class="rounded pa-3" style="position: relative; background-color: rgba(
                          var(--v-theme-on-background),
                          var(--v-hover-opacity)
                        ) !important;">
                          <AppTextField
                            v-model="findFormFieldValue(form_field.id).value"
                            type="number"
                            step="0.1"
                            :label="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :placeholder="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :rules="form_field.is_required ? [requiredValidator] : []"
                          />
                          <div style="position: absolute; top: -10px; right: 10px;" v-if="canManageCustomFields">
                            <VBtn
                              @click.prevent="editFieldSettings(form_field)"
                              class="mr-1"
                              size="25"
                              icon="tabler-settings"
                              color="dark"
                              rounded
                              variant="tonal"
                            />
                            <VBtn
                              color="dark"
                              @click.prevent="removeFormField(form_field.id)"
                              size="25"
                              icon="tabler-minus"
                              rounded
                              variant="tonal"
                            />
                          </div>
                        </div>
                        <div v-else-if="form_field.type == 'currency'" class="rounded pa-3" style="position: relative; background-color: rgba(
                          var(--v-theme-on-background),
                          var(--v-hover-opacity)
                        ) !important;">
                          <AppTextField
                            v-model="findFormFieldValue(form_field.id).value"
                            :label="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :placeholder="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :rules="form_field.is_required ? [requiredValidator] : []"
                          />
                          <div style="position: absolute; top: -10px; right: 10px;" v-if="canManageCustomFields">
                            <VBtn
                              @click.prevent="editFieldSettings(form_field)"
                              class="mr-1"
                              size="25"
                              icon="tabler-settings"
                              color="dark"
                              rounded
                              variant="tonal"
                            />
                            <VBtn
                              color="dark"
                              @click.prevent="removeFormField(form_field.id)"
                              size="25"
                              icon="tabler-minus"
                              rounded
                              variant="tonal"
                            />
                          </div>
                        </div>
                        <div v-else-if="form_field.type == 'date'" class="rounded pa-3" style="position: relative; background-color: rgba(
                          var(--v-theme-on-background),
                          var(--v-hover-opacity)
                        ) !important;">
                          <AppDateTimePicker
                            v-model="findFormFieldValue(form_field.id).value"
                            :config="dateTimePickerConfig"
                            :label="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :placeholder="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :rules="form_field.is_required ? [requiredValidator] : []"
                          />
                          <div style="position: absolute; top: -10px; right: 10px;" v-if="canManageCustomFields">
                            <VBtn
                              @click.prevent="editFieldSettings(form_field)"
                              class="mr-1"
                              size="25"
                              icon="tabler-settings"
                              color="dark"
                              rounded
                              variant="tonal"
                            />
                            <VBtn
                              color="dark"
                              @click.prevent="removeFormField(form_field.id)"
                              size="25"
                              icon="tabler-minus"
                              rounded
                              variant="tonal"
                            />
                          </div>
                        </div>
                        <div v-else-if="form_field.type == 'date_time'" class="rounded pa-3" style="position: relative; background-color: rgba(
                          var(--v-theme-on-background),
                          var(--v-hover-opacity)
                        ) !important;">
                          <AppDateTimePicker
                            v-model="findFormFieldValue(form_field.id).value"
                            :config="dateTimePickerConfig"
                            :label="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :placeholder="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :rules="form_field.is_required ? [requiredValidator] : []"
                          />
                          <div style="position: absolute; top: -10px; right: 10px;" v-if="canManageCustomFields">
                            <VBtn
                              @click.prevent="editFieldSettings(form_field)"
                              class="mr-1"
                              size="25"
                              icon="tabler-settings"
                              color="dark"
                              rounded
                              variant="tonal"
                            />
                            <VBtn
                              color="dark"
                              @click.prevent="removeFormField(form_field.id)"
                              size="25"
                              icon="tabler-minus"
                              rounded
                              variant="tonal"
                            />
                          </div>
                        </div>
                        <div v-else-if="form_field.type == 'email'" class="rounded pa-3" style="position: relative; background-color: rgba(
                          var(--v-theme-on-background),
                          var(--v-hover-opacity)
                        ) !important;">
                          <AppTextField
                            v-model="findFormFieldValue(form_field.id).value"
                            type="email"
                            :label="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :placeholder="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :rules="form_field.is_required ? [requiredValidator, emailValidator] : [emailValidator]"
                          />
                          <div style="position: absolute; top: -10px; right: 10px;" v-if="canManageCustomFields">
                            <VBtn
                              @click.prevent="editFieldSettings(form_field)"
                              class="mr-1"
                              size="25"
                              icon="tabler-settings"
                              color="dark"
                              rounded
                              variant="tonal"
                            />
                            <VBtn
                              color="dark"
                              @click.prevent="removeFormField(form_field.id)"
                              size="25"
                              icon="tabler-minus"
                              rounded
                              variant="tonal"
                            />
                          </div>
                        </div>
                        <div v-else-if="form_field.type == 'phone'" class="rounded pa-3" style="position: relative; background-color: rgba(
                          var(--v-theme-on-background),
                          var(--v-hover-opacity)
                        ) !important;">
                          <AppTextField
                            v-model="findFormFieldValue(form_field.id).value"
                            @input="(e) => onCustomPhoneInput(e, form_field.id)"
                            :label="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :placeholder="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :rules="form_field.is_required ? [requiredValidator, phoneValidator] : [phoneValidator]"
                          />
                          <div style="position: absolute; top: -10px; right: 10px;" v-if="canManageCustomFields">
                            <VBtn
                              @click.prevent="editFieldSettings(form_field)"
                              class="mr-1"
                              size="25"
                              icon="tabler-settings"
                              color="dark"
                              rounded
                              variant="tonal"
                            />
                            <VBtn
                              color="dark"
                              @click.prevent="removeFormField(form_field.id)"
                              size="25"
                              icon="tabler-minus"
                              rounded
                              variant="tonal"
                            />
                          </div>
                        </div>
                        <div v-else-if="form_field.type == 'pick_list'" class="rounded pa-3" style="position: relative; background-color: rgba(
                          var(--v-theme-on-background),
                          var(--v-hover-opacity)
                        ) !important;">
                          <AppSelect
                            @update:modelValue="customFieldPickListUpdated(form_field)"
                            v-model="findFormFieldValue(form_field.id).value"
                            :label="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :items="transformedOptions(form_field)"
                            :placeholder="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :rules="form_field.is_required ? [requiredValidator] : []"
                          />
                          <div style="position: absolute; top: -10px; right: 10px;" v-if="canManageCustomFields">
                            <VBtn
                              @click.prevent="editFieldSettings(form_field)"
                              class="mr-1"
                              size="25"
                              icon="tabler-settings"
                              color="dark"
                              rounded
                              variant="tonal"
                            />
                            <VBtn
                              color="dark"
                              @click.prevent="removeFormField(form_field.id)"
                              size="25"
                              icon="tabler-minus"
                              rounded
                              variant="tonal"
                            />
                          </div>
                        </div>
                        <div v-else-if="form_field.type == 'multi_select'" class="rounded pa-3" style="position: relative; background-color: rgba(
                          var(--v-theme-on-background),
                          var(--v-hover-opacity)
                        ) !important;">
                          <AppSelect
                            v-model="findFormFieldValue(form_field.id).value"
                            :label="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :items="transformedOptions(form_field)"
                            :placeholder="form_field.is_required ? form_field.name + ' *' : form_field.name"
                            :rules="form_field.is_required ? [requiredValidator] : []"
                            chips
                            multiple
                            closable-chips
                          />
                          <div style="position: absolute; top: -10px; right: 10px;" v-if="canManageCustomFields">
                            <VBtn
                              @click.prevent="editFieldSettings(form_field)"
                              class="mr-1"
                              size="25"
                              icon="tabler-settings"
                              color="dark"
                              rounded
                              variant="tonal"
                            />
                            <VBtn
                              color="dark"
                              @click.prevent="removeFormField(form_field.id)"
                              size="25"
                              icon="tabler-minus"
                              rounded
                              variant="tonal"
                            />
                          </div>
                        </div>
                      </VCol>
                    </VRow>
                  </div>
                </VWindowItem>

                <VWindowItem :key="1" :value="`item-${1}`">
                  <div class="py-5">
                    <VAlert type="info" variant="tonal" class="mb-4">
                      Branch codes and branch assignments are managed from the dedicated Branch Codes page.
                    </VAlert>
                    <VBtn prepend-icon="tabler-code" @click="router.push({ name: 'branch-codes', query: { investment_company_id: companyData.id } })">
                      Manage Branch Codes
                    </VBtn>
                  </div>
                </VWindowItem>

                <VWindowItem v-if="false" :key="1" :value="`item-${1}`">
                  <div class="py-5">
                    <div style="text-align: right">
                      <VBtn size="small" class="mb-2" @click="addBranchCode">
                        Add
                      </VBtn>
                    </div>

                    <VList lines="2">
                      <VListItem
                        v-for="(branch_code, index) in branchCodes"
                        :key="index"
                        style="
                          background-color: rgba(
                            var(--v-theme-on-background),
                            var(--v-hover-opacity)
                          ) !important;
                        "
                        class="rounded pa-3 px-10 mb-3"
                      >
                        <VListItemTitle>
                          <VRow>
                            <VCol cols="12" md="6">
                              <AppTextField
                                v-model="branchCodes[index].title"
                                rows="1"
                                row-height="15"
                                label="Title"
                                placeholder="Title"
                              />
                            </VCol>
                            <VCol cols="12" md="6">
                              <AppTextField
                                rows="1"
                                row-height="15"
                                v-model="branchCodes[index].branch_code"
                                label="Branch Code"
                                placeholder="Branch Code"
                              />
                            </VCol>
                            <VCol cols="12" md="12">
                              <AppTextField
                                v-model="branchCodes[index].description"
                                rows="1"
                                row-height="15"
                                label="Description"
                                placeholder="Description"
                              />
                            </VCol>
                            <VCol cols="12" md="12">
                              <AppCombobox
                                v-model="branchCodes[index].branches"
                                :items="branchList"
                                label="Select Branches"
                                placeholder="Type to search.."
                                :return-object="false"
                                clearable
                                multiple
                                chips
                                closable-chips
                                clear-icon="tabler-x"
                                @keyup="searchBranches"
                                @blur="validateBranches(index)"
                              />
                            </VCol>
                          </VRow>
                        </VListItemTitle>
                        <div
                          style="
                            position: absolute;
                            top: 0;
                            right: 0;
                            margin-top: -5px;
                            margin-right: 10px;
                          "
                        >
                          <VBtn
                            color="secondary"
                            icon
                            size="x-small"
                            @click="removeBranchCode(index)"
                          >
                            <VIcon icon="tabler-trash" />
                          </VBtn>
                        </div>
                      </VListItem>
                    </VList>
                  </div>
                </VWindowItem>

                <VWindowItem :key="2" :value="`item-${2}`">
                  <div class="py-5">
                    <VAlert type="info" variant="tonal" class="mb-4">
                      Rep codes and user assignments are managed from the dedicated Rep Codes page.
                    </VAlert>
                    <VBtn prepend-icon="tabler-key" @click="router.push({ name: 'rep-codes', query: { investment_company_id: companyData.id } })">
                      Manage Rep Codes
                    </VBtn>
                  </div>
                </VWindowItem>

                <VWindowItem v-if="false" :key="2" :value="`item-${2}`">
                  <div class="py-5">
                    <div style="text-align: right">
                      <VBtn size="small" class="mb-2" @click="addRepCode">
                        Add
                      </VBtn>
                    </div>

                    <VList lines="2">
                      <VListItem
                        v-for="(rep_code, index) in repCodes"
                        :key="index"
                        style="
                          background-color: rgba(
                            var(--v-theme-on-background),
                            var(--v-hover-opacity)
                          ) !important;
                        "
                        class="rounded pa-3 px-10 mb-3"
                      >
                        <VListItemTitle>
                          <VRow>
                            <VCol class="" cols="12" md="4">
                              <AppTextField
                                v-model="repCodes[index].title"
                                label="Title"
                                placeholder="Title"
                              />
                            </VCol>
                            <VCol class="" cols="12" md="4">
                              <AppTextField
                                v-model="repCodes[index].rep_code"
                                label="Rep Code"
                                placeholder="Rep Code"
                              />
                            </VCol>

                            <VCol class="" cols="12" md="4">
                              <AppSelect
                                v-model="
                                  repCodes[index]
                                    .investment_company_branch_code_id
                                "
                                :items="
                                  branchCodes.map((branch_code) => {
                                    return {
                                      title: branch_code.title,
                                      value: branch_code.id
                                        ? branch_code.id
                                        : branch_code.temp_id,
                                    };
                                  })
                                "
                                label="Branch Code"
                                placeholder="Branch Code"
                                clearable
                              />
                            </VCol>

                            <VCol class="" cols="12" md="4">
                              <AppCombobox
                                v-model="repCodes[index].branch_id"
                                :items="branchList"
                                label="Select Branch Office"
                                placeholder="Type to search.."
                                :return-object="false"
                                clearable
                                clear-icon="tabler-x"
                                @keyup="searchBranches"
                                @blur="validateBranches(index)"
                              />
                            </VCol>

                            <VCol class="" cols="12" md="4">
                              <AppSelect
                                v-model="repCodes[index].status"
                                :items="[
                                  { title: 'Active', value: 'active' },
                                  { title: 'Closed', value: 'closed' }
                                ]"
                                label="Status"
                                placeholder="Select Status"
                                clearable
                              />
                            </VCol>

                            <VCol class="" cols="12" md="12">
                              <AppCombobox
                                v-model="repCodes[index].users"
                                :items="userList"
                                label="Select Rep Users"
                                placeholder="Type to search.."
                                :return-object="false"
                                clearable
                                multiple
                                chips
                                closable-chips
                                clear-icon="tabler-x"
                                @keyup="searchUsers"
                                @blur="validateUsers(index)"
                              />
                            </VCol>
                            <VCol class="" cols="12" md="12">
                              <AppTextField
                                v-model="repCodes[index].description"
                                label="Description"
                                placeholder="Description"
                              />
                            </VCol>
                          </VRow>
                        </VListItemTitle>
                        <div
                          style="
                            position: absolute;
                            top: 0;
                            right: 0;
                            margin-top: -5px;
                            margin-right: 10px;
                          "
                        >
                          <VBtn
                            color="secondary"
                            icon
                            size="x-small"
                            @click="removeRepCode(index)"
                          >
                            <VIcon icon="tabler-trash" />
                          </VBtn>
                        </div>
                      </VListItem>
                    </VList>
                  </div>
                </VWindowItem>
              </VWindow>
            </VForm>
          </VCardText>
        </VCard>
      </VCol>

      <VCol md="3" cols="12" v-if="canManageCustomFields">
        <div class="system_fields sticky">
          <div class="d-flex flex-column justify-center mb-5">
            <h2 class="oswald">
              <strong>Custom Fields</strong>
            </h2>
            <div class="lato-light">Drag &amp; drop fields</div>
          </div>

          <div
            v-for="system_form_field_type of system_form_field_types"
            :key="system_form_field_type.type"
          >
            <div
              class="form_field_create_investment_companies bg-primary rounded mb-1 px-2 py-2"
              :type="system_form_field_type.type"
              style="-ms-touch-action: none; touch-action: none;"
            >
              <div>
                <VIcon
                  size="20"
                  :icon="resolveIconForFieldType(system_form_field_type)"
                  class="mr-3"
                />
                {{ system_form_field_type.name }}
              </div>
            </div>
          </div>
        </div>
      </VCol>
    </VRow>
  </div>

  <VDialog v-model="customFieldDialog" max-width="600">
    <DialogCloseBtn @click="customFieldDialog = !customFieldDialog" />
    <VCard>
      <VCardText>
        <VTabs v-model="customFieldDialogTab">
          <VTab>General Settings</VTab>
          <VTab
            v-if="customFormFieldInstance.type == 'pick_list' || customFormFieldInstance.type == 'multi_select'"
          >
            Field Options
          </VTab>
          <VTab>Layer Settings</VTab>
        </VTabs>
        <VWindow v-model="customFieldDialogTab">
          <VWindowItem :key="1" :value="`item-${1}`">
            <div class="py-5">
              <AppTextField
                v-model="customFormFieldInstance.name"
                label="Field Name"
                placeholder="Field Name"
              />
              <VSwitch
                class="mt-3"
                v-model="customFormFieldInstance.is_required"
                label="Is required"
              />
            </div>
          </VWindowItem>

          <VWindowItem
            class="py-5"
            v-if="customFormFieldInstance.type == 'pick_list' || customFormFieldInstance.type == 'multi_select'"
            :key="2"
            :value="`item-${2}`"
          >
            <div class="border rounded pa-2 mt-3 mb-5">
              <VSwitch
                @change="fieldLookupSettingChanged"
                v-model="customFormFieldInstance.is_lookup_field"
                label="Is Lookup Field"
              />
              <AppSelect
                class="mt-2"
                v-if="customFormFieldInstance.is_lookup_field"
                v-model="customFormFieldInstance.table"
                :items="modules"
                placeholder="Select Module"
                label="Module *"
                density="compact"
              />
              <AppSelect
                class="mt-2"
                v-if="customFormFieldInstance.is_lookup_field"
                v-model="customFormFieldInstance.column"
                :items="lookupFieldAttributes"
                placeholder="Select Attribute"
                label="Attribute *"
                density="compact"
              />
            </div>

            <div
              style="width: 100%; text-align: left"
              v-if="!customFormFieldInstance.is_lookup_field"
            >
              <AppTextField
                v-model="currentFieldOptionValue"
                label="Option Name"
                placeholder="Option Name"
              >
                <template #append>
                  <VBtn
                    @click.prevent="addFormFieldOption"
                    class="mr-1"
                    size="40"
                    icon="tabler-plus"
                    rounded
                  />
                </template>
              </AppTextField>
              <div class="mt-5 text-sm" v-if="customFormFieldInstance.options.length > 0">
                Current Options:
              </div>
              <VList
                style="padding: 0px !important"
                class="mt-1"
                v-if="customFormFieldInstance.options.length > 0"
              >
                <template
                  v-for="(form_field_option, index) of customFormFieldInstance.options"
                  :key="index"
                >
                  <VListItem class="px-1 ma-1 my-2 rounded" border>
                    <VListItemTitle class="pl-3">
                      {{
                        form_field_option.id
                          ? form_field_option.name
                          : form_field_option
                      }}
                    </VListItemTitle>
                    <template #append>
                      <VBtn
                        color="dark"
                        @click.prevent="removeFormFieldOption(index)"
                        size="30"
                        icon="tabler-minus"
                        rounded
                        variant="tonal"
                      />
                    </template>
                  </VListItem>
                </template>
              </VList>
            </div>
          </VWindowItem>

          <VWindowItem :key="2" :value="`item-${2}`">
            <div class="mb-2 mt-5 border rounded py-3 px-5">
              <label for="">Which branch users this field is for?</label>
              <VRadioGroup inline v-model="customFormFieldInstance.meta.layer_option.branches">
                <VRadio label="All" value="all" />
                <VRadio label="None" value="none" />
                <VRadio label="Custom" value="custom" />
              </VRadioGroup>
              <AppAutocomplete
                v-if="customFormFieldInstance.meta.layer_option.branches == 'custom'"
                @keyup="searchBranchesForField"
                v-model="customFormFieldInstance.branches"
                :items="branchesForField"
                label="Select Branch"
                placeholder="Type to search.."
                :return-object="false"
                clearable
                clear-icon="tabler-x"
              />
            </div>

            <div class="mb-2 mt-5 border rounded py-3 px-5">
              <label for="">Which role users this field is for?</label>
              <VRadioGroup inline v-model="customFormFieldInstance.meta.layer_option.roles">
                <VRadio label="All" value="all" />
                <VRadio label="None" value="none" />
                <VRadio label="Custom" value="custom" />
              </VRadioGroup>
              <AppSelect
                v-if="customFormFieldInstance.meta.layer_option.roles == 'custom'"
                v-model="customFormFieldInstance.roles"
                :items="rolesForField"
                label="Select Roles"
                placeholder="Type to search.."
                :return-object="false"
                clearable
                multiple
                chips
                closable-chips
                clear-icon="tabler-x"
              />
            </div>
          </VWindowItem>
        </VWindow>

        <div class="d-flex flex-wrap justify-center gap-4 mt-5">
          <VBtn type="button" block @click="saveFormFieldChanges">
            Save Changes
          </VBtn>
        </div>
      </VCardText>
    </VCard>
  </VDialog>

  <DeleteConfirm
    v-model:deleteDialog="deleteFormFieldDialog"
    @closeDelete="deleteFormFieldDialog = false"
    @deleteItemConfirm="removeFormFieldSubmit"
  />
</template>


<style scoped>
.draggable-col-investment-companies-edit {
  cursor: grab;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.draggable-col-investment-companies-edit:active {
  cursor: grabbing;
}

.draggable-col-investment-companies-edit * {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.drop-area-bg {
  border: 2px dotted #ccc !important;
  background-color: rgba(var(--v-theme-on-background), var(--v-hover-opacity)) !important;
}

.sticky {
  position: -webkit-sticky;
  position: sticky;
  top: 5rem;
  z-index: 1;
}
</style>
